import { Button, Input, Select } from "antd";
import { ColumnsType } from "antd/es/table";
import { Link } from "react-router-dom";
import { useAppSelector } from "../../../app/store";
import SearchIcon from "../../../assets/icons/SearchIcon";
import SelectDownIcon from "../../../assets/icons/SelectDownIcon";
import SortIcon from "../../../assets/icons/SortIcon";
import ViewIcon from "../../../assets/icons/ViewIcon";
import IMG from "../../../assets/images/avatar.svg";
import CustomTable from "../../../components/ui/CustomTable";
import { dbDateFormat } from "../../../constants/constants";
import { useGetDepartmentsQuery } from "../../../features/department/departmentApi";
import { useGetDesignationsQuery } from "../../../features/designation/designationApi";
import { useGetEmployeeTrackingAlternativeQuery } from "../../../features/employeeTracking/employeeTrackingApi";
import useError from "../../../hooks/useError";
import useTable from "../../../hooks/useTable";
import SidebarLayout from "../../../layouts/SidebarLayout";
import { formatDate } from "../../../lib/time";
import {
  EmployeeTrackingDataResponseType,
  EmployeeTrackingParams,
} from "../../../types/tracking";
import { countParPageSerialNumber } from "../../../utils/number";
import {
  addKeyInArray,
  selectArrayOption,
  selectSearchOption,
  underscoreToCapitalizeString,
} from "../../../utils/utils";

const searchTypeSelect = [
  { label: "Name", value: "name" },
  { label: "Email", value: "email" },
];

export default function EmployeeTracking() {
  const {
    page,
    limit,
    filter,
    handlePagination,
    handleLimit,
    handleSearch,
    handleFilter,
    keyword,
  } = useTable<EmployeeTrackingParams>({
    defaultFilter: {
      searchType: "name",
    },
  });
  const { authInfo } = useAppSelector((state) => state.auth);
  const {
    isError: isDepartmentError,
    error: departmentError,
    data: departmentData,
  } = useGetDepartmentsQuery({
    params: { skip: 0, limit: 0, is_published: 1 },
  });
  const {
    data: designationData,
    isError: isDesignationError,
    error: designationError,
  } = useGetDesignationsQuery(
    {
      params: {
        limit: 0,
        skip: 0,
        ...(filter?.department_id
          ? { business_department_id: filter?.department_id?.toString() || "" }
          : {}),
      },
    },
    {
      skip: !filter?.department_id,
    }
  );
  const { isLoading, isFetching, isError, error, data } =
    useGetEmployeeTrackingAlternativeQuery(
      {
        params: {
          ...filter,
          searchType: undefined,
          ...(keyword ? { searchType: filter.searchType } : {}),
          searchText: keyword,
          business_id: authInfo?.business_id || 0,
        },
      },
      {
        skip: !authInfo?.business_id,
      }
    );
  useError(isError, error, "Get employee tracking data fail");
  useError(isDepartmentError, departmentError, "Get department data fail");
  useError(isDesignationError, designationError, "Get designation fail");

  console.log(data);

  // const findTrackingData = (id: number) => {
  //   return data?.data?.trackingData?.find(
  //     (el) => el?.business_member_id === id
  //   );
  // };

  const columns: ColumnsType<
    EmployeeTrackingDataResponseType["data"]["businesses"][0]["businessMembers"][0]
  > = [
    {
      title: "#SL",
      align: "center",
      width: 60,
      render: (_, __, index) => countParPageSerialNumber(page, limit, index),
    },
    {
      title: "Employee",
      sortIcon: () => <SortIcon />,
      sorter: (a, b) =>
        a?.member?.profile?.name?.localeCompare(b?.member?.profile?.name),
      render: (val) => {
        return (
          <div className="flex items-center gap-3">
            <div className="w-10 h-10">
              <img
                className="w-full h-full rounded-full"
                src={val?.member?.profile?.pro_pic || IMG}
                alt=""
              />
            </div>
            <div className="flex-1 flex flex-col gap-[2px] text-sm font-medium text-display-body">
              <h6 className="text-body text-base font-semibold">
                {val?.member?.profile?.name}
              </h6>
              <p className="text-extra-gray">ID: {val?.employee_id || "n/a"}</p>
              <p>{val?.role?.name || "Designation: n/a"}</p>
              <p>
                Email:{" "}
                {val?.member?.profile?.email ? (
                  <a
                    className="!text-accent hover:underline"
                    href={`mailto:${val?.member?.profile?.email}`}
                  >
                    {val?.member?.profile?.email}
                  </a>
                ) : (
                  "n/a"
                )}
              </p>
            </div>
          </div>
        );
      },
    },
    {
      title: "Last Activity",
      width: 200,
      // render: (val) => {
      //   const activityDate = findTrackingData(val?.id);
      //   return activityDate ? (
      //     <>
      //       {moment(activityDate?.time, "hh:mm:ss").format(timeFormat)},{" "}
      //       {formatDate(activityDate?.date)}
      //     </>
      //   ) : (
      //     "n/a"
      //   );
      // },
      dataIndex: "latest_tracking_date",
      render: (val) => {
        return val ? <>{formatDate(val, dbDateFormat)}</> : "n/a";
      },
    },
    {
      title: "Last Location",
      width: 400,
      // render: (val) => {
      //   const activityAddress = findTrackingData(val?.id)?.location;
      //   const parseJson =
      //     typeof activityAddress === "string"
      //       ? (parseJSON(activityAddress) as LocationType)
      //       : activityAddress;

      //   return activityAddress ? parseJson?.address : "n/a";
      // },
      dataIndex: "latest_tracking",
      render: (val) => val?.location?.address || "n/a",
    },
    {
      title: "Action",
      // dataIndex: "member",
      align: "center",
      render: (val) => (
        <Link to={`view/${val?.id}`}>
          <Button className="action-button" size="small" icon={<ViewIcon />} />
        </Link>
      ),
    },
  ];

  return (
    <SidebarLayout title="Live Tracking">
      <CustomTable
        loading={isLoading || isFetching}
        limit={limit}
        page={page}
        columns={columns}
        total={data?.data?.total}
        dataSource={addKeyInArray(data?.data?.businesses?.[0]?.businessMembers)}
        handleLimit={handleLimit}
        handlePagination={handlePagination}
        filterSection={
          <>
            <Select
              showSearch
              placeholder="Search Type"
              value={filter.searchType}
              options={searchTypeSelect}
              onChange={(val) => handleFilter("searchType", val)}
              filterOption={selectSearchOption}
              className="min-w-[100px]"
              suffixIcon={<SelectDownIcon />}
              rootClassName="icon-gray"
            />
            <Input
              suffix={<SearchIcon />}
              placeholder={`Search by ${underscoreToCapitalizeString(
                filter.searchType || "",
                true
              )}`}
              style={{ minWidth: 170, maxWidth: 180 }}
              onChange={(val) => handleSearch(val)}
            />
            <Select
              allowClear
              showSearch
              onChange={(e) => handleFilter("department_id", e)}
              placeholder="Department"
              filterOption={selectSearchOption}
              options={selectArrayOption(
                departmentData?.data?.departments || [],
                "name",
                "id"
              )}
              className="min-w-[130px]"
              suffixIcon={<SelectDownIcon />}
              rootClassName="icon-gray"
            />
            <Select
              allowClear
              showSearch
              placeholder="Designation"
              filterOption={selectSearchOption}
              options={selectArrayOption(
                designationData?.data?.roles || [],
                "name",
                "id"
              )}
              className="min-w-[130px]"
              suffixIcon={<SelectDownIcon />}
              rootClassName="icon-gray"
              onChange={(e) => handleFilter("designation_id", e)}
              disabled={!filter?.department_id}
            />
            <Select
              allowClear
              showSearch
              onChange={(e) => handleFilter("no_activity", e)}
              placeholder="Activity"
              filterOption={selectSearchOption}
              options={activityList}
              className="min-w-[130px] max-w-[150px]"
              suffixIcon={<SelectDownIcon />}
              rootClassName="icon-gray select-full-text"
            />
          </>
        }
      />
    </SidebarLayout>
  );
}

const activityList = [
  { label: "No activity since 1 hour", value: 60 },
  { label: "No activity since 2 hour", value: 120 },
  { label: "No activity since 4 hour", value: 240 },
  { label: "No activity since 6 hour", value: 360 },
  { label: "No activity since 8 hour", value: 480 },
];
